import React, { useMemo } from 'react';
import { CampfireRichEditor } from '@campfire/campfire-rich-editor';
import { unpackToDateTime } from '@campfire/hot-date';
import { TabletButton } from '@campfire/tablet-button';
import { Box, CircularProgress, Divider, IconButton, Tooltip, Typography } from '@material-ui/core';
import { BorderColorOutlined, Close, LabelImportantRounded } from '@material-ui/icons';
import { EditorState } from 'draft-js';
import { isUndefined } from 'lodash';
import { useUser } from '../../../global/auth/useUser';
import { useCampfireQuery } from '../../../global/network/useCampfireQuery';
import { FallbackAvatar } from '../../general/user-profile/components/Avatar';
import { Reaction } from '../../../common/reaction/Reaction';
import { useSaveReaction } from '../../../common/reaction/reaction-model';
import { CommentsList } from '../../general/vol-dashboard/vol-dashboard-my-elements/main-section/my-recent-news/CommentsLists';
import { BULLETIN_COMMENT } from '../../general/vol-dashboard/vol-dashboard-my-elements/main-section/my-recent-news/bulletin-comment.gql';
import { ALLOW_COMMENT_CONFIG_ITEM_QUERY } from '../../general/vol-dashboard/vol-dashboard-my-elements/main-section/my-recent-news/my-news-model.gql';
import {
  BulletinComments,
  BulletinCommentsVariables,
} from '../../general/vol-dashboard/vol-dashboard-my-elements/main-section/my-recent-news/__generated__/BulletinComments';
import { GetAllowCommentConfigItem } from '../../general/vol-dashboard/vol-dashboard-my-elements/main-section/my-recent-news/__generated__/GetAllowCommentConfigItem';
import { BulletinManagement } from './__generated__/BulletinManagement';
import { audienceNames, bulletinAuthor, bulletinContentState } from './bulletin-model';
import { PANEL_WIDTH, PINNED_AMBER } from './bulletinStyles';

interface BulletinPanelProps {
  bulletin: BulletinManagement;
  canUserAddEdit: boolean;
  isPinning: boolean;
  onClose: () => void;
  onEdit: () => void;
  onTogglePinned: () => void;
}

const initialsOf = (bulletin: BulletinManagement) => {
  const author = bulletin.lastUpdatedBy ?? bulletin.createdBy;
  if (!author) return null;
  return `${author.profile.preferredName.charAt(0)}${author.profile.lastName.charAt(0)}`.toUpperCase();
};

export const BulletinPanel = ({
  bulletin,
  canUserAddEdit,
  isPinning,
  onClose,
  onEdit,
  onTogglePinned,
}: BulletinPanelProps) => {
  const { getVolunteerIdentity } = useUser();
  const saveReaction = useSaveReaction();

  const editorState = useMemo(() => {
    const contentState = bulletinContentState(bulletin.content);
    return contentState ? EditorState.createWithContent(contentState) : EditorState.createEmpty();
  }, [bulletin.content]);

  const { data, refetch } = useCampfireQuery<BulletinComments, BulletinCommentsVariables>(BULLETIN_COMMENT, {
    options: { variables: { bulletinId: bulletin.bulletinId } },
  });

  const { data: vmAllowComment } = useCampfireQuery<GetAllowCommentConfigItem, {}>(ALLOW_COMMENT_CONFIG_ITEM_QUERY, {});

  const names = audienceNames(bulletin);
  const audience = names.length === 0 ? 'Everyone' : names.join(', ');

  const isEnableComment = isUndefined(vmAllowComment?.vm.configItem?.configItemValue?.flagValue)
    ? Boolean(vmAllowComment?.vm.configItem?.defaultValue)
    : Boolean(vmAllowComment?.vm.configItem?.configItemValue?.flagValue);

  const comments = data?.vm.bulletin.comments ?? [];
  const reactions = data?.vm.bulletin.reactions ?? [];
  const canComment = Boolean(bulletin.isCommentable && isEnableComment);
  const hideAllComments = !canComment && comments.length === 0;

  // Reacting needs a volunteer identity, which an admin-only account does not have.
  const canReact = Boolean(getVolunteerIdentity());

  const handleReaction = (symbol: string, reactionId?: string) => {
    saveReaction({ bulletinId: bulletin.bulletinId, emoji: symbol, reactionId }).then(() => {
      if (refetch) refetch();
    });
  };

  return (
    <Box
      width={PANEL_WIDTH}
      flexShrink={0}
      display='flex'
      flexDirection='column'
      minHeight={0}
      style={{ borderLeft: '1px solid #EFEFEF', backgroundColor: '#FFFFFF' }}
    >
      <Box
        flexShrink={0}
        display='flex'
        alignItems='center'
        justifyContent='space-between'
        padding='12px 24px'
        style={{ gap: 12, borderBottom: '1px solid #EFEFEF' }}
      >
        <Box display='flex' alignItems='center' minWidth={0} style={{ gap: 8 }}>
          {bulletin.pinned && (
            <Box
              display='flex'
              alignItems='center'
              flexShrink={0}
              style={{
                backgroundColor: PINNED_AMBER,
                color: '#FFFFFF',
                borderRadius: 4,
                padding: '2px 8px 2px 6px',
                gap: 4,
              }}
            >
              <LabelImportantRounded style={{ fontSize: 13 }} />
              <Typography style={{ fontSize: 11, fontWeight: 600 }}>Pinned</Typography>
            </Box>
          )}
          <Tooltip title={audience}>
            <Typography noWrap style={{ fontSize: 12.5, color: '#646464' }}>
              {audience}
            </Typography>
          </Tooltip>
        </Box>

        <Box display='flex' alignItems='center' flexShrink={0} style={{ gap: 8 }}>
          {canUserAddEdit && (
            <TabletButton
              size='small'
              variant='outlined'
              disabled={isPinning}
              onClick={onTogglePinned}
              startIcon={
                isPinning ? <CircularProgress size={13} /> : <LabelImportantRounded style={{ fontSize: 15 }} />
              }
              style={{
                border: '1px solid #9e9e9e',
                borderRadius: 4,
                padding: '5px 10px',
                textTransform: 'none',
                color: '#646464',
                whiteSpace: 'nowrap',
              }}
            >
              {bulletin.pinned ? 'Unpin' : 'Pin'}
            </TabletButton>
          )}

          {canUserAddEdit && (
            <TabletButton
              size='small'
              variant='contained'
              color='primary'
              onClick={onEdit}
              startIcon={<BorderColorOutlined style={{ fontSize: 15 }} />}
              style={{ borderRadius: 4, textTransform: 'none', padding: '5px 12px' }}
            >
              Edit
            </TabletButton>
          )}

          <IconButton size='small' aria-label='Close bulletin' onClick={onClose}>
            <Close style={{ fontSize: 18, color: '#646464' }} />
          </IconButton>
        </Box>
      </Box>

      <Box flexGrow={1} minHeight={0} padding='26px 24px' style={{ overflowY: 'auto', boxSizing: 'border-box' }}>
        <Typography style={{ fontSize: 22, fontWeight: 800, color: '#3E3229', lineHeight: 1.3 }}>
          {bulletin.title}
        </Typography>

        <Box display='flex' alignItems='center' style={{ gap: 10, margin: '12px 0 20px 0' }}>
          <FallbackAvatar size={28} initials={initialsOf(bulletin)} style={{ flexShrink: 0 }} />
          <Typography style={{ fontSize: 12.5, color: '#646464' }}>{bulletinAuthor(bulletin)}</Typography>
          <Typography style={{ fontSize: 12.5, color: '#9E9E9E' }}>
            {unpackToDateTime(bulletin.dateCreated).toFormat('d LLLL yyyy')}
          </Typography>
        </Box>

        <CampfireRichEditor editorState={editorState} readOnly />

        {canReact && (
          <Box marginTop='16px'>
            <Reaction reactions={reactions} handleReaction={handleReaction} />
          </Box>
        )}

        {!hideAllComments && (
          <Box marginTop='24px'>
            <Typography style={{ fontSize: 13, fontWeight: 700, color: '#3E3229' }}>Comments</Typography>
            <Divider style={{ margin: '12px 0 16px 0' }} />
            <CommentsList
              bulletinId={bulletin.bulletinId}
              comments={comments}
              hideCommentBox={!canComment}
              refetch={refetch}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};
